import {
  LoadingManager,
  Texture,
  TextureLoader,
  SRGBColorSpace,
  LinearSRGBColorSpace,
  type BufferGeometry,
} from 'three';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';

export type ColorSpaceHint = 'srgb' | 'linear';

export class AssetLoader {
  private manager: LoadingManager;
  private textureCache = new Map<string, Texture>();
  private geometryCache = new Map<string, BufferGeometry>();
  private textureLoader: TextureLoader;
  private objLoader: OBJLoader;
  private gltfLoader: GLTFLoader;
  private rgbeLoader: RGBELoader;

  constructor() {
    this.manager = new LoadingManager();
    this.textureLoader = new TextureLoader(this.manager);
    this.objLoader = new OBJLoader(this.manager);
    this.gltfLoader = new GLTFLoader(this.manager);
    this.rgbeLoader = new RGBELoader(this.manager);
  }

  async loadTexture(url: string, colorSpace: ColorSpaceHint = 'srgb'): Promise<Texture> {
    const cached = this.textureCache.get(url);
    if (cached) return cached;

    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => {
          texture.colorSpace = colorSpace === 'srgb' ? SRGBColorSpace : LinearSRGBColorSpace;
          texture.flipY = true;
          this.textureCache.set(url, texture);
          resolve(texture);
        },
        undefined,
        reject,
      );
    });
  }

  async loadOBJ(url: string): Promise<BufferGeometry> {
    const cached = this.geometryCache.get(url);
    if (cached) return cached;

    return new Promise((resolve, reject) => {
      this.objLoader.load(
        url,
        (group) => {
          const mesh = group.children[0];
          if (!mesh || !('geometry' in mesh)) {
            reject(new Error(`No mesh found in OBJ: ${url}`));
            return;
          }
          const geometry = (mesh as { geometry: BufferGeometry }).geometry;
          geometry.computeBoundingSphere();
          this.geometryCache.set(url, geometry);
          resolve(geometry);
        },
        undefined,
        reject,
      );
    });
  }

  async loadGLTF(url: string): Promise<GLTF> {
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(url, resolve, undefined, reject);
    });
  }

  async loadEnvironmentMap(url: string): Promise<Texture> {
    return /\.(jpg|jpeg|png|webp)$/i.test(url)
      ? this.loadTexture(url, 'srgb')
      : this.loadHDRI(url);
  }

  async loadHDRI(url: string): Promise<Texture> {
    const cached = this.textureCache.get(url);
    if (cached) return cached;

    return new Promise((resolve, reject) => {
      this.rgbeLoader.load(
        url,
        (texture) => {
          this.textureCache.set(url, texture);
          resolve(texture);
        },
        undefined,
        reject,
      );
    });
  }

  dispose(): void {
    for (const texture of this.textureCache.values()) {
      texture.dispose();
    }
    for (const geometry of this.geometryCache.values()) {
      geometry.dispose();
    }
    this.textureCache.clear();
    this.geometryCache.clear();
  }
}
